<template>
    <span v-if="!session.is_completed">
        {{ $t("virtual_visit:start_a_call:description") }}:
        <a class="btn btn-default btn-xs" @click="openSMS">{{ $t("virtual_visit:send_sms:btn") }}</a>
        {{ $t("general:or") }}
        <a class="btn btn-default btn-xs" @click="openEmail">{{ $t("virtual_visit:send_email:btn") }}</a>.
        {{ $t("virtual_visit:sms_email_verification:description") }}

        <modal
            :name="smsModalName"
            :scrollable="true"
            height="auto"
            width="500px"
            :pivot-y="0.05"
            :classes="['ivc-modal']"
            @before-close="onSMSModalClose"
        >
            <div class="modal-content">
                <div class="modal-header">
                    <button type="button" class="close" @click="$modal.hide(smsModalName)">&times;</button>
                    <h4 class="modal-title">{{ $t("virtual_visit:send_sms:btn") }}</h4>
                </div>

                <div class="modal-body">
                    <p>{{ $t("virtual_visit:send_sms:description") }}</p>

                    <div class="form-group">
                        <label for="ivc-sms-mobile">{{ $t("virtual_visit:phone_number") }}</label>
                        <input
                            id="ivc-sms-mobile"
                            v-model.trim="smsMobile"
                            type="tel"
                            class="form-control"
                            placeholder="+38631456789"
                            autocomplete="tel"
                        />
                    </div>

                    <div class="form-group">
                        <div class="ivc-preview-label-row">
                            <label class="ivc-preview-label">{{ $t("virtual_visit:message_preview") }}</label>
                            <span class="ivc-lang">
                                <button ref="smsChip" type="button" class="ivc-lang__chip" :title="$t('virtual_visit:language')" @click.stop="toggleLang('sms')">
                                    <span class="ivc-lang__flag">{{ langFlag(smsLanguage) }}</span>
                                    <span class="ivc-lang__code">{{ langCode(smsLanguage) }}</span>
                                    <i class="fa fa-caret-down"></i>
                                </button>
                                <ul v-if="langOpen === 'sms'" class="ivc-lang__menu" :style="menuStyle">
                                    <li v-for="l in languages" :key="l.code">
                                        <button type="button" :class="['ivc-lang__option', { 'is-active': l.code === smsLanguage }]" @click="selectLang('sms', l.code)">
                                            <span class="ivc-lang__flag">{{ l.flag }}</span>
                                            <span>{{ l.label }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </span>
                        </div>
                        <textarea
                            class="form-control ivc-body-textarea"
                            rows="5"
                            readonly
                            :value="smsBody"
                        ></textarea>
                    </div>

                    <div v-if="sentMessages.length" class="form-group">
                        <label class="ivc-preview-label">{{ $t("virtual_visit:send_sms:sent_list") }}</label>
                        <ul class="ivc-sent">
                            <li
                                v-for="m in sentMessages"
                                :key="m.pk"
                                :class="['ivc-sent__item', 'is-' + rowState(m)]"
                                :title="m.error_name"
                            >
                                <i :class="statusIcon(m)"></i>
                                <span class="ivc-sent__to">{{ m.to }}</span>
                                <span class="ivc-sent__status">{{ statusLabel(m) }}</span>
                            </li>
                        </ul>
                        <p v-if="hasUnconfirmed" class="ivc-sent__hint">
                            {{ $t("virtual_visit:send_sms:unconfirmed_hint") }}
                        </p>
                    </div>

                    <p v-if="smsError" class="ivc-error">
                        <i class="fa fa-exclamation-triangle"></i> {{ smsError }}
                    </p>

                    <div class="text-right">
                        <button type="button" class="btn btn-default" @click="$modal.hide(smsModalName)">
                            {{ $t("general:cancel:btn") }}
                        </button>
                        <button type="button" class="btn btn-info" :disabled="smsSending || !smsMobile" @click="sendSMS">
                            <i v-if="smsSending" class="fa fa-spinner fa-spin"></i>
                            {{ $t("general:send:btn") }}
                        </button>
                    </div>
                </div>
            </div>
        </modal>

        <modal
            :name="emailModalName"
            :scrollable="true"
            height="auto"
            width="550px"
            :classes="['ivc-modal']"
            @before-close="onModalClose"
        >
            <div class="modal-content">
                <div class="modal-header">
                    <button type="button" class="close" @click="$modal.hide(emailModalName)">&times;</button>
                    <h4 class="modal-title">{{ $t("virtual_visit:send_email:btn") }}</h4>
                </div>

                <div class="modal-body">
                    <p>{{ $t("virtual_visit:send_email:description") }}</p>

                    <div class="form-group">
                        <label for="ivc-email-to">{{ $t("virtual_visit:send_email:recipient") }}</label>
                        <input
                            id="ivc-email-to"
                            v-model.trim="emailTo"
                            type="email"
                            class="form-control"
                            placeholder="ime.priimek@example.com"
                            autocomplete="email"
                        />
                    </div>

                    <div class="form-group">
                        <label for="ivc-email-subject">{{ $t("virtual_visit:send_email:subject_label") }}</label>
                        <input
                            id="ivc-email-subject"
                            v-model="emailSubject"
                            type="text"
                            class="form-control"
                            maxlength="200"
                        />
                    </div>

                    <div class="form-group">
                        <div class="ivc-preview-label-row">
                            <label class="ivc-preview-label">{{ $t("virtual_visit:message_preview") }}</label>
                            <span class="ivc-lang">
                                <button ref="emailChip" type="button" class="ivc-lang__chip" :title="$t('virtual_visit:language')" @click.stop="toggleLang('email')">
                                    <span class="ivc-lang__flag">{{ langFlag(emailLanguage) }}</span>
                                    <span class="ivc-lang__code">{{ langCode(emailLanguage) }}</span>
                                    <i class="fa fa-caret-down"></i>
                                </button>
                                <ul v-if="langOpen === 'email'" class="ivc-lang__menu" :style="menuStyle">
                                    <li v-for="l in languages" :key="l.code">
                                        <button type="button" :class="['ivc-lang__option', { 'is-active': l.code === emailLanguage }]" @click="selectLang('email', l.code)">
                                            <span class="ivc-lang__flag">{{ l.flag }}</span>
                                            <span>{{ l.label }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </span>
                        </div>
                        <textarea
                            v-model="emailBody"
                            class="form-control ivc-body-textarea"
                            rows="8"
                        ></textarea>
                    </div>

                    <p v-if="emailError" class="ivc-error">
                        <i class="fa fa-exclamation-triangle"></i> {{ emailError }}
                    </p>

                    <div class="text-right">
                        <button type="button" class="btn btn-default" @click="$modal.hide(emailModalName)">
                            {{ $t("general:close") }}
                        </button>
                        <button type="button" :class="['btn', emailCopied ? 'btn-success' : 'btn-default']" @click="copyEmail">
                            <span v-if="emailCopied"><i class="fa fa-check"></i> {{ $t("general:copied") }}</span>
                            <span v-else><i class="fa fa-copy"></i> {{ $t("general:copy") }}</span>
                        </button>
                        <button type="button" class="btn btn-info" :disabled="emailSending || !emailTo" @click="sendEmail">
                            <i v-if="emailSending" class="fa fa-spinner fa-spin"></i>
                            {{ $t("general:send:btn") }}
                        </button>
                    </div>
                </div>
            </div>
        </modal>
    </span>
</template>

<script>
import ApiService from '@/components/utils/api.service.js';
import { trackCallEvent } from './callEvents.js';

const LANGUAGES = [
    { code: "sl-SI", flag: "🇸🇮", label: "Slovenščina" },
    { code: "hr",    flag: "🇭🇷", label: "Hrvatski" },
    { code: "en",    flag: "🇬🇧", label: "English" },
    { code: "cs",    flag: "🇨🇿", label: "Čeština" },
    { code: "de",    flag: "🇩🇪", label: "Deutsch" },
    { code: "hu",    flag: "🇭🇺", label: "Magyar" },
    { code: "ro",    flag: "🇷🇴", label: "Română" },
];

const UI_LOCALE_TO_LANG = {
    sl: "sl-SI", "sl-SI": "sl-SI",
    hr: "hr", en: "en", cs: "cs", de: "de", hu: "hu", ro: "ro",
};

let _instanceId = 0;

// A rejection lands within seconds, so check often while something is still
// waiting, then settle down - a late report can arrive for hours.
const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 15000;

// Rejections come back at once, but a delivery report can take 20+ minutes on
// some operators (one Slovenian number reported 23 minutes after the send), so
// three minutes of silence is the point where waiting stops being informative. The provider itself keeps trying for 48h, which is no help
// during a call. This only changes what is displayed - the row stays PENDING
// and a late report still corrects it.
const CONFIRMATION_TIMEOUT_MS = 180000;

const STATE_LABELS = {
    waiting: "pending",
    unconfirmed: "unconfirmed",
    sent: "sent",
    delivered: "delivered",
    failed: "failed",
};

const STATE_ICONS = {
    waiting: "fa fa-clock-o text-muted",
    unconfirmed: "fa fa-exclamation-triangle text-warning",
    sent: "fa fa-check text-muted",
    delivered: "fa fa-check-circle text-success",
    failed: "fa fa-times-circle text-danger",
};

export default {
    props: {
        session: { type: Object, required: true },
        teamLanguage: { type: String, default: "" },
    },
    data() {
        const uiLocale = (this.$i18n && this.$i18n.locale) || "sl";
        // The moderator's team language is the default; fall back to the UI locale.
        const defaultLang = UI_LOCALE_TO_LANG[this.teamLanguage]
            || UI_LOCALE_TO_LANG[uiLocale]
            || "sl-SI";
        const id = ++_instanceId;
        return {
            languages: LANGUAGES,
            smsModalName: `ivc-sms-${id}`,
            emailModalName: `ivc-email-${id}`,
            smsLanguage: defaultLang,
            emailLanguage: defaultLang,
            smsMobile: "",
            smsSending: false,
            smsError: "",
            emailError: "",
            sentMessages: [],
            emailTo: "",
            emailSubject: "",
            emailBody: "",
            emailSending: false,
            emailCopied: false,
            langOpen: null,
            langMenuPos: null,
            _outsideHandler: null,
            _windowHandler: null,
            _copiedTimer: null,
            _pollTimer: null,
            nowTs: Date.now(),
        };
    },
    computed: {
        clientUrl() {
            return this.session.client_url_full;
        },
        smsBody() {
            const template = this.$i18n.t("virtual_visit:allow_camera_access", this.vueLocale(this.smsLanguage));
            const url = this.clientUrl ? `${this.clientUrl}?lang=${this.smsLanguage}` : "";
            return template.replace(/%s%s/, url);
        },
        // Seeds the editable body. Recomputed on open and on every language change,
        // or switching SL -> EN would leave the Slovenian text in place.
        defaultEmailBody() {
            const locale = this.vueLocale(this.emailLanguage);
            const content = this.$i18n.t("virtual_visit:send_email:content", locale);
            const greeting = this.$i18n.t("virtual_visit:send_email:greeting", locale);
            const url = this.clientUrl ? `${this.clientUrl}?lang=${this.emailLanguage}` : "";
            return `${content}\n\n${url}\n\n${greeting}.`;
        },
        defaultEmailSubject() {
            return this.$i18n.t("virtual_visit:send_email:subject", this.vueLocale(this.emailLanguage));
        },
        hasWaiting() {
            return this.sentMessages.some((m) => this.rowState(m) === "waiting");
        },
        hasUnconfirmed() {
            return this.sentMessages.some((m) => this.rowState(m) === "unconfirmed");
        },
        menuStyle() {
            if (!this.langMenuPos) {
                return { position: "fixed", visibility: "hidden" };
            }
            return {
                position: "fixed",
                top: this.langMenuPos.top + "px",
                right: this.langMenuPos.right + "px",
            };
        },
    },
    methods: {
        vueLocale(code) {
            return code === "sl-SI" ? "sl" : code;
        },
        langFlag(code) {
            const l = this.languages.find((x) => x.code === code);
            return l ? l.flag : "";
        },
        langCode(code) {
            return (code || "").split("-")[0].toUpperCase();
        },
        openSMS() {
            this.langOpen = null;
            this.smsError = "";
            this.loadSentMessages();
            // Poll while the dialog is open, not only after a send - an operator
            // reopening it to check on an earlier invite needs it to move too.
            this.startPolling();
            this.$modal.show(this.smsModalName);
        },
        openEmail() {
            this.langOpen = null;
            this.emailCopied = false;
            this.emailError = "";
            this.resetEmailDraft();
            this.$modal.show(this.emailModalName);
        },
        resetEmailDraft() {
            this.emailSubject = this.defaultEmailSubject;
            this.emailBody = this.defaultEmailBody;
        },
        toggleLang(which) {
            if (this.langOpen === which) {
                this.langOpen = null;
                this.langMenuPos = null;
                return;
            }
            this.langOpen = which;
            this.langMenuPos = null;
            this.$nextTick(() => this.updateMenuPos());
        },
        updateMenuPos() {
            if (!this.langOpen) return;
            const ref = this.langOpen === "sms" ? this.$refs.smsChip : this.$refs.emailChip;
            if (!ref) return;
            const r = ref.getBoundingClientRect();
            this.langMenuPos = {
                top: Math.round(r.bottom + 4),
                right: Math.round(window.innerWidth - r.right),
            };
        },
        selectLang(which, code) {
            if (which === "sms") {
                this.smsLanguage = code;
            } else {
                this.emailLanguage = code;
                // Body and subject are editable, so they no longer follow the
                // language on their own - anything typed is discarded here.
                this.resetEmailDraft();
            }
            this.langOpen = null;
            this.langMenuPos = null;
        },
        onModalClose() {
            this.langOpen = null;
            this.langMenuPos = null;
        },
        onSMSModalClose() {
            // Only the SMS dialog owns the poll - closing the e-mail dialog must
            // not stop it, since both modals can be open at once.
            this.onModalClose();
            this.stopPolling();
        },
        loadSentMessages() {
            return ApiService
                .get(`/virtual-visit/api/virtual-session/${this.session.id}/invite-client-sms`)
                .then((response) => {
                    this.sentMessages = response.data || [];
                    // Advances the clock the timeout is measured against, so a row
                    // flips to "unconfirmed" within one poll of the deadline.
                    this.nowTs = Date.now();
                })
                .catch(() => { /* the invite itself still works without its history */ });
        },
        startPolling() {
            this.stopPolling();
            const tick = () => {
                this.loadSentMessages();
                // Fast only while something can still resolve. After that a late
                // report may yet arrive, so keep looking, just rarely.
                this._pollTimer = setTimeout(tick, this.hasWaiting ? POLL_FAST_MS : POLL_SLOW_MS);
            };
            this._pollTimer = setTimeout(tick, POLL_FAST_MS);
        },
        stopPolling() {
            if (this._pollTimer) {
                clearTimeout(this._pollTimer);
                this._pollTimer = null;
            }
        },
        rowState(message) {
            if (message.failed) return "failed";
            if (message.status === "DELIVERED") return "delivered";
            if (message.status !== "PENDING") return "sent";
            return this.ageMs(message) >= CONFIRMATION_TIMEOUT_MS ? "unconfirmed" : "waiting";
        },
        ageMs(message) {
            const created = new Date(message.created).getTime();
            return Number.isNaN(created) ? 0 : this.nowTs - created;
        },
        statusLabel(message) {
            return this.$t(`virtual_visit:send_sms:status:${STATE_LABELS[this.rowState(message)]}`);
        },
        statusIcon(message) {
            return STATE_ICONS[this.rowState(message)];
        },
        sendSMS() {
            if (!this.smsMobile || this.smsSending) return;
            this.smsSending = true;
            this.smsError = "";
            ApiService
                .post(`/virtual-visit/api/virtual-session/${this.session.id}/send-invite-client-sms`, {
                    mobile: this.smsMobile,
                    language: this.smsLanguage,
                })
                .then(() => {
                    trackCallEvent("invite_sms_sent", { language: this.smsLanguage });
                    this.smsSending = false;
                    this.notify(this.$t("virtual_visit:sms_sent"), "", "success");
                    // Sent only means the gateway took it. Keep the modal open so
                    // the delivery status has somewhere to appear (#3925).
                    this.smsMobile = "";
                    this.loadSentMessages();
                    this.startPolling();
                })
                .catch((error) => {
                    trackCallEvent("invite_sms_failed", {
                        http_status: error.response && error.response.status,
                        status: error.response && error.response.data && error.response.data.status,
                    });
                    this.smsSending = false;
                    // Every failure arrives as a 400 with a "status" - reading only
                    // the code made an expired link read as an invalid number.
                    const data = (error.response && error.response.data) || {};
                    let msg = this.$t("virtual_visit:send_sms:error:general");
                    if (error.response && error.response.status === 403) {
                        msg = this.$t("virtual_visit:send_sms:error:no_permissions");
                    } else if (data.status === "INVALID") {
                        msg = this.$t("virtual_visit:send_sms:error:invalid_number");
                    } else if (data.status === "EXPIRED") {
                        msg = this.$t("virtual_visit:send_sms:error:link_expired");
                    } else if (data.status === "UNREACHABLE") {
                        msg = this.$t("virtual_visit:send_sms:error:unreachable");
                    }
                    // Shown in the dialog as well as the toast: the operator is
                    // looking at the form, and on the setup page there was no
                    // toast target at all, so a rejection read as nothing (#3925).
                    this.smsError = msg;
                    this.notify(this.$t("virtual_visit:send_sms:not_sent"), msg, "warn");
                });
        },
        sendEmail() {
            if (!this.emailTo || this.emailSending) return;
            this.emailSending = true;
            ApiService
                .post(`/virtual-visit/api/virtual-session/${this.session.id}/send-invite-client-email`, {
                    email: this.emailTo,
                    subject: this.emailSubject,
                    body: this.emailBody,
                    language: this.emailLanguage,
                })
                .then(() => {
                    trackCallEvent("invite_email_sent", { language: this.emailLanguage });
                    this.emailSending = false;
                    this.notify(this.$t("virtual_visit:send_email:sent"), "", "success");
                    // Nothing is recorded for e-mail, so there is no status to stay open for.
                    this.$modal.hide(this.emailModalName);
                })
                .catch((error) => {
                    trackCallEvent("invite_email_failed", {
                        http_status: error.response && error.response.status,
                        status: error.response && error.response.data && error.response.data.status,
                    });
                    this.emailSending = false;
                    const data = (error.response && error.response.data) || {};
                    let msg = this.$t("virtual_visit:send_email:error:general");
                    if (error.response && error.response.status === 403) {
                        msg = this.$t("virtual_visit:send_email:error:no_permissions");
                    } else if (data.status === "INVALID") {
                        msg = this.$t("virtual_visit:send_email:error:invalid");
                    } else if (data.status === "EXPIRED") {
                        msg = this.$t("virtual_visit:send_email:error:link_expired");
                    }
                    this.emailError = msg;
                    this.notify(this.$t("virtual_visit:send_email:not_sent"), msg, "warn");
                });
        },
        copyEmail() {
            const text = this.emailBody;
            const showCopied = () => {
                this.emailCopied = true;
                if (this._copiedTimer) clearTimeout(this._copiedTimer);
                this._copiedTimer = setTimeout(() => { this.emailCopied = false; }, 1500);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(showCopied).catch(() => this._fallbackCopy(text, showCopied));
            } else {
                this._fallbackCopy(text, showCopied);
            }
        },
        _fallbackCopy(text, onSuccess) {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.top = "-1000px";
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try {
                document.execCommand("copy");
                onSuccess();
            } catch (e) {
                // noop
            }
            document.body.removeChild(ta);
        },
        notify(title, text, type) {
            this.$notify({ group: "OTwarnings", type: type, title: title, text: text });
        },
    },
    mounted() {
        this._outsideHandler = (e) => {
            if (this.langOpen && !e.target.closest(".ivc-lang") && !e.target.closest(".ivc-lang__menu")) {
                this.langOpen = null;
                this.langMenuPos = null;
            }
        };
        this._windowHandler = () => {
            if (this.langOpen) {
                this.langOpen = null;
                this.langMenuPos = null;
            }
        };
        document.addEventListener("click", this._outsideHandler);
        window.addEventListener("resize", this._windowHandler);
    },
    beforeDestroy() {
        if (this._outsideHandler) {
            document.removeEventListener("click", this._outsideHandler);
        }
        if (this._windowHandler) {
            window.removeEventListener("resize", this._windowHandler);
        }
        if (this._copiedTimer) clearTimeout(this._copiedTimer);
        this.stopPolling();
    },
};
</script>

<style scoped>
.ivc-preview-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5em;
    margin-bottom: 5px;
}
.ivc-preview-label {
    margin: 0;
}
.ivc-sent {
    list-style: none;
    margin: 5px 0 0;
    padding: 0;
    /* One row per send, so this grows without bound - cap it and scroll inside,
       or a long call pushes the modal past the top of the viewport. */
    max-height: 8.5em;
    overflow-y: auto;
}
.ivc-sent__item {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.25em 0;
    font-size: 0.95em;
}
.ivc-sent__item.is-failed {
    color: #a94442;
}
.ivc-sent__item.is-unconfirmed {
    color: #8a6d3b;
}
.ivc-error {
    margin: 0.6em 0 0;
    color: #a94442;
}
.ivc-sent__hint {
    margin: 0.4em 0 0;
    font-size: 0.9em;
    color: #8a6d3b;
}
.ivc-sent__to {
    font-family: monospace;
}
.ivc-sent__status {
    color: #777;
}
.ivc-sent__item.is-failed .ivc-sent__status,
.ivc-sent__item.is-unconfirmed .ivc-sent__status {
    color: inherit;
}
.ivc-lang {
    position: relative;
    display: inline-block;
}
.ivc-lang__chip {
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 999px;
    padding: 0.2em 0.7em;
    font-size: 0.95em;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    cursor: pointer;
}
.ivc-lang__chip:hover {
    background: #ececec;
}
.ivc-lang__flag {
    font-size: 1.1em;
    line-height: 1;
}
.ivc-lang__code {
    font-weight: 600;
    letter-spacing: 0.05em;
}
.ivc-lang__menu {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    list-style: none;
    margin: 0;
    padding: 4px 0;
    min-width: 180px;
    z-index: 10000;
}
.ivc-lang__option {
    background: none;
    border: 0;
    width: 100%;
    text-align: left;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    gap: 0.5em;
    cursor: pointer;
    color: inherit;
    font: inherit;
}
.ivc-lang__option:hover {
    background: #f5f5f5;
}
.ivc-lang__option.is-active {
    font-weight: 600;
}
.ivc-body-textarea {
    text-align: left;
    font-family: inherit;
    resize: vertical;
    background: #fafafa;
    white-space: pre-wrap;
}
.text-right .btn + .btn {
    margin-left: 6px;
}
</style>
